import React, {useEffect, useLayoutEffect, useRef, useState} from "react";

import {
    Alert,
    Button,
    Card,
    ConfigProvider,
    Flex,
    Popconfirm,
    Progress,
    Result,
    Statistic,
    Steps,
    Tag,
    Timeline,
    Tooltip,
    Typography,
} from "antd";
import {
    CalendarOutlined,
    ClockCircleOutlined,
    DownloadOutlined,
    HourglassOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
    StopOutlined,
    SyncOutlined,
    UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const {Text, Title} = Typography;

// Вес этапов в общем прогрессе; считается по невыключенным этапам и пересчитывается на 100.
// compare — этап сравнения копий All List (страница «Сравнение All List»).
const STAGE_WEIGHTS = {sales: 40, listings: 5, warehouse: 5, zoro: 40, excel: 10, compare: 15};
// Быстрые этапы: если их обычная длительность неизвестна, в оценке оставшегося времени считаем их мгновенными.
const QUICK_STAGES = ["listings", "warehouse"];
// Уже этой ширины панели этапы показываются вертикально.
const COMPACT_WIDTH = 720;
const BASE_TITLE = "Amazon Reports";
const BAR_GRADIENT = {from: "#7c4dff", to: "#b388ff"};

const isZeroTime = (iso) => !iso || String(iso).startsWith("0001");
const toMs = (iso) => (isZeroTime(iso) ? NaN : Date.parse(iso));
const pad2 = (n) => String(n).padStart(2, "0");

const intFmt = new Intl.NumberFormat("ru-RU");
const moneyFmt = new Intl.NumberFormat("ru-RU", {minimumFractionDigits: 2, maximumFractionDigits: 2});
const fmtInt = (v) => intFmt.format(Math.round(Number(v) || 0));
const fmtMoney = (v) => moneyFmt.format(Number(v) || 0);
const fmtTime = (iso) => (isZeroTime(iso) ? "" : dayjs(iso).utcOffset(180).format("HH:mm:ss"));
const fmtHourMin = (iso) => (isZeroTime(iso) ? "" : dayjs(iso).utcOffset(180).format("HH:mm"));

// Секунды -> «2:35» или «1:02:35».
const fmtClock = (sec) => {
    const s = Math.max(0, Math.floor(Number(sec) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}:${pad2(m)}:${pad2(s % 60)}` : `${m}:${pad2(s % 60)}`;
};

// Секунды -> «45 с», «1 мин 52 с», «1 ч 5 мин».
export const fmtDuration = (sec) => {
    const s = Math.max(0, Math.round(Number(sec) || 0));
    if (s < 60) return `${s} с`;
    if (s < 3600) {
        const rest = s % 60;
        return rest ? `${Math.floor(s / 60)} мин ${rest} с` : `${Math.floor(s / 60)} мин`;
    }
    let h = Math.floor(s / 3600);
    let m = Math.round((s % 3600) / 60);
    if (m === 60) {
        h += 1;
        m = 0;
    }
    return m ? `${h} ч ${m} мин` : `${h} ч`;
};

const fmtEta = (sec) => {
    if (sec < 60) return "осталось меньше минуты";
    const mins = Math.ceil(sec / 60);
    if (mins < 60) return `осталось ~${mins} мин`;
    const m = mins % 60;
    return `осталось ~${Math.floor(mins / 60)} ч${m ? ` ${m} мин` : ""}`;
};

// «выложено N · неактивно N · не выложено N · All List не загружен N»; нулевые части (кроме «выложено») не показываем.
export const fmtListingSummary = (s = {}) =>
    [
        `выложено ${fmtInt(s.Listed)}`,
        Number(s.Inactive) > 0 ? `неактивно ${fmtInt(s.Inactive)}` : "",
        Number(s.NotListed) > 0 ? `не выложено ${fmtInt(s.NotListed)}` : "",
        Number(s.ListingUnknown) > 0 ? `All List не загружен ${fmtInt(s.ListingUnknown)}` : "",
    ].filter(Boolean).join(" · ");

export const LISTING_HINT =
    "По последним All List (отчёты All Listings из manager.enjoytool.net). Неактивно — ASIN есть в All List, но Inactive везде; " +
    "не выложено — нет ни в одном All List; All List не загружен — у магазинов, где ASIN продавался, All List нет. " +
    "Магазины, отмеченные неактивными на странице «Сравнение All List», не учитываются.";
export const ZORO_FAILED_HINT = "Сбой — Zoro не ответил или заблокировал запросы (номер не найден и после повторной попытки).";
export const CANCEL_PENDING_HINT = "Отмена запрошена — отчёт остановится после текущего шага";

const stageDurationSec = (s) => {
    const a = toMs(s?.StartedAt);
    const b = toMs(s?.FinishedAt);
    return Number.isNaN(a) || Number.isNaN(b) || b < a ? null : (b - a) / 1000;
};

// Длительность всей задачи: от запуска (или постановки в очередь) до окончания.
export const jobDurationSec = (job) => {
    const started = toMs(job?.StartedAt);
    const start = Number.isNaN(started) ? toMs(job?.CreatedAt) : started;
    const end = toMs(job?.FinishedAt);
    return Number.isNaN(start) || Number.isNaN(end) || end < start ? null : (end - start) / 1000;
};

const typicalOf = (typical, key) => {
    const v = Number(typical?.[key]);
    return v > 0 ? v : null;
};

const elapsedSec = (iso, nowMs) => {
    const t = toMs(iso);
    return Number.isNaN(t) ? null : Math.max(0, (nowMs - t) / 1000);
};

// Progress задачи относится к текущему этапу.
const stageCounter = (stage, job) => {
    const total = Number(job.Progress?.Total) || 0;
    const isCurrent = !job.Stage || job.Stage === stage.Key;
    return isCurrent && total > 0 ? {done: Math.min(total, Number(job.Progress?.Done) || 0), total} : null;
};

// Доля выполнения идущего этапа (0..1).
const stageFraction = (stage, job, typical, nowMs) => {
    const counter = stageCounter(stage, job);
    if (counter) return counter.done / counter.total;
    const typ = typicalOf(typical, stage.Key);
    const elapsed = elapsedSec(stage.StartedAt, nowMs);
    if (typ && elapsed !== null) return Math.min(0.95, elapsed / typ);
    return 0.3;
};

const computePercent = (stages, job, typical, nowMs) => {
    let total = 0;
    let done = 0;
    stages.forEach((s) => {
        if (s.Status === "skipped") return;
        const w = STAGE_WEIGHTS[s.Key] ?? 10;
        total += w;
        if (s.Status === "done") done += w;
        else if (s.Status === "running") done += w * stageFraction(s, job, typical, nowMs);
    });
    return total > 0 ? (done / total) * 100 : 0;
};

// Оценка оставшегося времени в секундах; null — оценить нельзя.
const computeEta = (stages, job, typical, nowMs) => {
    const run = stages.find((s) => s.Status === "running");
    if (!run) return null;
    const elapsed = elapsedSec(run.StartedAt, nowMs);

    let current = null;
    const counter = stageCounter(run, job);
    if (run.Key === "zoro" && counter && elapsed !== null && elapsed >= 5) {
        // В Done сразу входят номера из кэша — скорость считаем только по реально запрошенным.
        const cached = Number(job.Stats?.ZoroCached) || 0;
        const fetched = counter.done - (cached <= counter.done ? cached : 0);
        if (counter.done >= counter.total) current = 0;
        else if (fetched > 0) current = (counter.total - counter.done) / (fetched / elapsed);
    }
    if (current === null) {
        const typ = typicalOf(typical, run.Key);
        if (typ && elapsed !== null) current = Math.max(0, typ - elapsed);
        else if (QUICK_STAGES.includes(run.Key)) current = 0;
        else return null;
    }

    let rest = 0;
    for (const s of stages) {
        if (s.Status !== "pending") continue;
        const typ = typicalOf(typical, s.Key);
        if (typ) rest += typ;
        else if (!QUICK_STAGES.includes(s.Key)) return null;
    }
    return current + rest;
};

const prefersReducedMotion = () =>
    typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function useElementWidth(ref) {
    const [width, setWidth] = useState(0);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        setWidth(el.clientWidth);
        let raf = 0;
        // Через requestAnimationFrame, чтобы не получить «ResizeObserver loop» при перестройке раскладки.
        const measure = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => setWidth(el.clientWidth));
        };
        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", measure);
            return () => {
                cancelAnimationFrame(raf);
                window.removeEventListener("resize", measure);
            };
        }
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [ref]);
    return width;
}

// Число, которое плавно «доезжает» до нового значения и коротко подсвечивается.
function AnimatedNumber({value, money}) {
    const target = Number(value) || 0;
    const [shown, setShown] = useState(0);
    const [glow, setGlow] = useState(false);
    const shownRef = useRef(0);

    useEffect(() => {
        const from = shownRef.current;
        if (from === target) {
            setGlow(false);
            return undefined;
        }
        if (prefersReducedMotion()) {
            shownRef.current = target;
            setShown(target);
            return undefined;
        }
        let raf = 0;
        let timer = 0;
        const t0 = performance.now();
        const duration = 900;
        setGlow(true);
        const tick = (t) => {
            const k = Math.min(1, (t - t0) / duration);
            shownRef.current = k >= 1 ? target : from + (target - from) * (1 - Math.pow(1 - k, 3));
            setShown(shownRef.current);
            if (k < 1) raf = requestAnimationFrame(tick);
            else timer = setTimeout(() => setGlow(false), 700);
        };
        raf = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(timer);
        };
    }, [target]);

    return <span className={glow ? "arp-num arp-num-glow" : "arp-num"}>{money ? fmtMoney(shown) : fmtInt(shown)}</span>;
}

const StepLines = ({lines}) => (
    <Flex vertical gap="1px">
        {lines.filter(Boolean).map((l, i) => (
            <span key={i}>{l}</span>
        ))}
    </Flex>
);

function buildStepItems(stages, job, typical, nowMs) {
    const cached = Number(job.Stats?.ZoroCached) || 0;
    return stages.map((s) => {
        const title = s.Label || s.Key;
        const dur = stageDurationSec(s);
        const typ = typicalOf(typical, s.Key);
        switch (s.Status) {
            case "done":
                return {
                    title,
                    status: "finish",
                    description: <StepLines lines={[s.Detail, dur !== null ? fmtDuration(dur) : ""]}/>,
                };
            case "running": {
                const counter = stageCounter(s, job);
                const elapsed = elapsedSec(s.StartedAt, nowMs);
                return {
                    title,
                    status: "process",
                    icon: <LoadingOutlined/>,
                    description: (
                        <Flex vertical gap="1px" className="arp-step-live">
                            {counter && (
                                <span className="arp-tabular">
                                    {fmtInt(counter.done)} из {fmtInt(counter.total)}
                                    {s.Key === "zoro" && cached > 0 ? ` · из кэша ${fmtInt(cached)}` : ""}
                                </span>
                            )}
                            {counter && (
                                <Progress
                                    percent={Math.floor((counter.done / counter.total) * 100)}
                                    showInfo={false}
                                    size={{height: 4}}
                                    strokeColor={BAR_GRADIENT}
                                    style={{margin: "2px 0", maxWidth: 170}}
                                    aria-label={`${title}: ${fmtInt(counter.done)} из ${fmtInt(counter.total)}`}
                                />
                            )}
                            {elapsed !== null && <span className="arp-tabular">идёт {fmtClock(elapsed)}</span>}
                            {!counter && typ && <span className="arp-muted">обычно ~{fmtDuration(typ)}</span>}
                        </Flex>
                    ),
                };
            }
            case "skipped":
                return {title, status: "wait", className: "arp-step-skipped", description: "не выбрано"};
            case "failed":
                return {
                    title,
                    status: "error",
                    description: <StepLines lines={[s.Detail || "ошибка", dur !== null ? fmtDuration(dur) : ""]}/>,
                };
            default:
                return {title, status: "wait", description: typ ? `обычно ~${fmtDuration(typ)}` : "ожидает"};
        }
    });
}

const counterCardStyle = {flex: "1 1 150px", minWidth: 140, background: "rgba(255,255,255,0.025)", border: "1px solid #2a2140", borderRadius: 12};

// Счётчики отчёта «Все ASIN с продажами» (по умолчанию, если страница не передала свои).
// Элемент: {key, title, value, stage, tip, money}; stage — этап, после которого значение готово.
const asinCounterItems = (job) => {
    const s = job.Stats || {};
    return [
        {key: "asins", title: "ASIN", value: s.Asins, stage: "sales"},
        {key: "units", title: "Продано, шт", value: s.Units, stage: "sales"},
        {key: "amount", title: "Сумма, $", value: s.Amount, stage: "sales", money: true},
        job.WithWarehouse && {
            key: "warehouse",
            title: "На складе",
            value: s.WarehouseMatched,
            stage: "warehouse",
            tip: `ASIN, найденных на складе; в наличии: ${fmtInt(s.WarehouseInStock)}`,
        },
        job.WithZoro && {
            key: "zoro",
            title: "Zoro найдено",
            value: s.ZoroFound,
            stage: "zoro",
            tip: (
                <>
                    <div>не найдено: {fmtInt(s.ZoroNotFound)} · сбой: {fmtInt(s.ZoroFailed)} · из кэша: {fmtInt(s.ZoroCached)}</div>
                    <div style={{opacity: 0.75, marginTop: 4}}>{ZORO_FAILED_HINT}</div>
                </>
            ),
        },
        job.WithListings && {
            key: "notlisted",
            title: "Не выложено",
            value: s.NotListed,
            stage: "listings",
            tip: (
                <>
                    <div>{fmtListingSummary(s)}</div>
                    <div style={{opacity: 0.75, marginTop: 4}}>{LISTING_HINT}</div>
                </>
            ),
        },
    ];
};

function Counters({job, isReady, counters}) {
    const built = typeof counters === "function" ? counters(job) : asinCounterItems(job);
    const items = (Array.isArray(built) ? built : []).filter(Boolean);

    return (
        <Flex gap="12px" wrap="wrap">
            {items.map((c) => {
                const ready = isReady(c.stage);
                const title = ready && c.tip ? (
                    <Tooltip title={c.tip} overlayStyle={{maxWidth: 420}}>
                        <span tabIndex={0} style={{cursor: "help"}}>
                            {c.title} <InfoCircleOutlined style={{fontSize: 12, opacity: 0.6}}/>
                        </span>
                    </Tooltip>
                ) : (
                    c.title
                );
                return (
                    <Card key={c.key} size="small" style={counterCardStyle} styles={{body: {padding: "12px 16px"}}}>
                        <Statistic
                            title={title}
                            value={ready ? Number(c.value) || 0 : "—"}
                            formatter={ready ? () => <AnimatedNumber value={c.value} money={c.money}/> : undefined}
                            valueStyle={{fontSize: 22, fontWeight: 600, color: ready ? "#efe7ff" : "rgba(255,255,255,0.3)"}}
                        />
                    </Card>
                );
            })}
        </Flex>
    );
}

function EventFeed({events, title}) {
    const seen = {};
    const keyed = events.map((e) => {
        const base = `${e?.At}|${e?.Text}`;
        seen[base] = (seen[base] || 0) + 1;
        return {e: e || {}, key: seen[base] > 1 ? `${base}#${seen[base]}` : base};
    });
    const last = keyed.length - 1;
    // Новые события — сверху.
    const items = keyed
        .map(({e, key}, i) => ({
            key,
            color: i === last ? "#b388ff" : "#4a3a6b",
            className: i === last ? "arp-ev-latest" : undefined,
            children: (
                <div className="arp-ev">
                    <span className="arp-ev-time">{fmtTime(e.At)}</span>
                    <span className="arp-ev-text">{e.Text}</span>
                </div>
            ),
        }))
        .reverse();

    return (
        <Flex vertical gap="8px">
            <Flex justify="space-between" align="baseline" gap="12px">
                <Text strong>{title}</Text>
                <Text type="secondary" style={{fontSize: 12}}>время МСК</Text>
            </Flex>
            <div className="arp-feed" role="log" aria-label="Журнал формирования отчёта" tabIndex={0}>
                <Timeline items={items}/>
            </div>
        </Flex>
    );
}

const WarningsAlert = ({warnings}) =>
    warnings.length > 0 ? (
        <Alert
            type="warning"
            showIcon
            message={`Предупреждения: ${warnings.length}`}
            description={
                <div style={{maxHeight: 160, overflowY: "auto"}}>
                    {warnings.map((w, i) => (
                        <div key={i} style={{marginBottom: 4}}>• {w}</div>
                    ))}
                </div>
            }
        />
    ) : null;

const PANEL_CSS = `
.arp-topline{height:3px;background:linear-gradient(90deg,rgba(124,77,255,0),#7c4dff,#b388ff,#7c4dff,rgba(124,77,255,0));background-size:200% 100%;animation:arp-slide 2.2s linear infinite}
@keyframes arp-slide{from{background-position:200% 0}to{background-position:0 0}}
.arp-pulse{position:relative;flex:none;width:12px;height:12px;border-radius:50%;background:#b388ff;box-shadow:0 0 12px rgba(179,136,255,.7)}
.arp-pulse::after{content:"";position:absolute;inset:0;border-radius:50%;background:#b388ff;animation:arp-ping 1.6s cubic-bezier(0,0,.2,1) infinite}
@keyframes arp-ping{0%{transform:scale(1);opacity:.65}80%,100%{transform:scale(2.8);opacity:0}}
.arp-tabular{font-variant-numeric:tabular-nums}
.arp-muted{opacity:.7}
.arp-num{display:inline-block;font-variant-numeric:tabular-nums;transition:color .8s ease,text-shadow .8s ease}
.arp-num-glow{color:#dcc6ff;text-shadow:0 0 14px rgba(179,136,255,.6)}
.arp-timer{font-variant-numeric:tabular-nums;font-size:14px;padding:3px 10px;margin-inline-end:0}
.arp-step-skipped{opacity:.42}
.arp-step-live{color:#d6c4f5}
.arp-feed{max-height:220px;overflow-y:auto;padding:8px 10px 0 4px;border-radius:10px;scrollbar-width:thin;font-size:13.5px}
.arp-feed .ant-timeline-item{padding-bottom:12px}
.arp-feed .ant-timeline-item-last{padding-bottom:0}
.arp-ev{display:inline-block;padding:1px 8px;margin-inline-start:-8px;border-radius:8px;transition:background-color 1.2s ease,color 1.2s ease}
.arp-ev-time{font-variant-numeric:tabular-nums;font-size:12px;color:rgba(255,255,255,.45);margin-inline-end:10px}
.arp-ev-latest .arp-ev{background:rgba(124,77,255,.14);color:#efe6ff;animation:arp-fade .6s ease-out}
@keyframes arp-fade{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion: reduce){.arp-topline,.arp-pulse::after,.arp-ev-latest .arp-ev{animation:none}.arp-num,.arp-ev{transition:none}}
`;

const panelBase = {width: "100%", borderRadius: 16, overflow: "hidden"};
const PANEL_STYLES = {
    live: {
        ...panelBase,
        border: "1px solid #3b2a66",
        background: "radial-gradient(120% 140% at 0% 0%, rgba(124,77,255,0.15) 0%, rgba(124,77,255,0) 55%), #181226",
        boxShadow: "0 12px 40px -20px rgba(124,77,255,0.6)",
    },
    done: {
        ...panelBase,
        border: "1px solid rgba(82,196,26,0.35)",
        background: "radial-gradient(120% 140% at 50% 0%, rgba(82,196,26,0.10) 0%, rgba(82,196,26,0) 55%), #181226",
    },
    failed: {
        ...panelBase,
        border: "1px solid rgba(255,77,79,0.35)",
        background: "radial-gradient(120% 140% at 50% 0%, rgba(255,77,79,0.08) 0%, rgba(255,77,79,0) 55%), #181226",
    },
    cancelled: {...panelBase, border: "1px solid #2a2140"},
};

/**
 * Живая панель хода формирования фонового отчёта («Все ASIN с продажами», «Сравнение All List»).
 * Пока задача в очереди или в работе — этапы, общий прогресс, счётчики и лента событий;
 * после окончания — итог (готово / ошибка / отменено) до нажатия «Скрыть».
 * title — название отчёта в заголовке вкладки браузера; counters — (job) => элементы счётчиков
 * (без него — счётчики отчёта ASIN).
 */
export default function AsinReportProgress({
    job,
    typical,
    serverOffset = 0,
    periodLabel,
    downloadHref,
    onCancel,
    cancelling = "",
    cancelPending = false,
    onDismiss,
    title = "Отчёт ASIN",
    counters,
}) {
    const active = job.Status === "queued" || job.Status === "running";
    const queued = job.Status === "queued";
    const stages = Array.isArray(job.Stages) && job.Stages.length > 0 ? job.Stages : null;
    const events = Array.isArray(job.Events) ? job.Events : [];
    const warnings = Array.isArray(job.Warnings) ? job.Warnings : [];

    const wrapRef = useRef(null);
    const width = useElementWidth(wrapRef);
    const compact = width > 0 && width < COMPACT_WIDTH;

    // Часы панели: тикают раз в секунду, только пока задача идёт.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!active) return undefined;
        setNow(Date.now());
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [active]);
    const serverNow = now + (Number(serverOffset) || 0);

    // Общий процент: по весам этапов; для одной и той же задачи никогда не уменьшается.
    const maxPct = useRef({id: "", pct: 0});
    let pct = null;
    if (active && stages) {
        let p = Math.max(0, Math.min(99, Math.floor(computePercent(stages, job, typical, serverNow))));
        if (maxPct.current.id === job.Id) p = Math.max(p, maxPct.current.pct);
        maxPct.current = {id: job.Id, pct: p};
        pct = p;
    }
    // Старые задачи без этапов: только счётчик текущего этапа, как в таблице.
    const legacyTotal = Number(job.Progress?.Total) || 0;
    const legacyPct = !stages && legacyTotal > 0
        ? Math.min(100, Math.floor(((Number(job.Progress?.Done) || 0) / legacyTotal) * 100))
        : null;

    const eta = active && !queued && stages ? computeEta(stages, job, typical, serverNow) : null;

    // Заголовок вкладки браузера: виден, даже когда пользователь на другой вкладке.
    let titlePrefix = "";
    if (active) {
        const shownPct = pct ?? legacyPct;
        if (queued) titlePrefix = `⏳ В очереди · ${title}`;
        else titlePrefix = shownPct !== null ? `⏳ ${shownPct}% · ${title}` : `⏳ ${title}`;
    } else if (job.Status === "done") {
        titlePrefix = "✅ Отчёт готов";
    } else if (job.Status === "failed") {
        titlePrefix = "⚠️ Ошибка отчёта";
    }
    const baseTitle = useRef(BASE_TITLE);
    useEffect(() => {
        const current = document.title || BASE_TITLE;
        const base = /^(⏳|✅|⚠)/.test(current) ? BASE_TITLE : current;
        baseTitle.current = base;
        return () => {
            document.title = base;
        };
    }, []);
    useEffect(() => {
        document.title = titlePrefix ? `${titlePrefix} — ${baseTitle.current}` : baseTitle.current;
    }, [titlePrefix]);

    const isReady = (key) =>
        stages ? stages.some((s) => s.Key === key && s.Status === "done") : job.Status === "done";

    const visibleStages = stages ? stages.filter((s) => s.Status !== "skipped") : [];
    const runIdx = visibleStages.findIndex((s) => s.Status === "running");
    const runStage = runIdx >= 0 ? visibleStages[runIdx] : null;

    const startedMs = toMs(job.StartedAt);
    const jobStart = queued || Number.isNaN(startedMs) ? toMs(job.CreatedAt) : startedMs;
    const elapsed = Number.isNaN(jobStart) ? null : Math.max(0, (serverNow - jobStart) / 1000);

    const pad = compact ? 18 : 26;
    const mode = active ? "live" : PANEL_STYLES[job.Status] ? job.Status : "cancelled";

    const meta = (
        <Flex gap="2px 14px" wrap="wrap">
            <Text type="secondary"><CalendarOutlined/> {periodLabel}</Text>
            {job.CreatedBy && <Text type="secondary"><UserOutlined/> запустил {job.CreatedBy}</Text>}
        </Flex>
    );

    const renderLive = () => {
        let stageLine = job.StageLabel || "Подготовка…";
        if (queued) stageLine = "Ожидает запуска";
        else if (runStage) stageLine = `Этап ${runIdx + 1} из ${visibleStages.length}: ${runStage.Label || runStage.Key}`;

        return (
            <Flex vertical gap={compact ? "16px" : "20px"}>
                <Flex justify="space-between" align="flex-start" gap="12px 20px" wrap="wrap">
                    <Flex gap="14px" align="center" style={{minWidth: 0}}>
                        <span className="arp-pulse" aria-hidden="true"/>
                        <Flex vertical gap="2px" style={{minWidth: 0}}>
                            <Title level={4} style={{margin: 0}}>{queued ? "Отчёт в очереди" : "Формируется отчёт"}</Title>
                            {meta}
                        </Flex>
                    </Flex>
                    <Flex gap="10px" align="center" wrap="wrap">
                        {elapsed !== null && (
                            <Tag className="arp-timer" color="purple" icon={<ClockCircleOutlined/>}>
                                {queued ? "в очереди" : "идёт"} {fmtClock(elapsed)}
                            </Tag>
                        )}
                        {cancelPending ? (
                            <>
                                <Tag className="arp-timer" color="warning" icon={<SyncOutlined spin/>}>отмена…</Tag>
                                <Tooltip title={CANCEL_PENDING_HINT}>
                                    <Button danger icon={<StopOutlined/>} disabled aria-label={CANCEL_PENDING_HINT}>
                                        Отменить
                                    </Button>
                                </Tooltip>
                            </>
                        ) : (
                            <Popconfirm
                                title="Отменить формирование отчёта?"
                                okText="Отменить отчёт"
                                okButtonProps={{danger: true}}
                                cancelText="Нет"
                                onConfirm={() => onCancel(job.Id)}
                            >
                                <Button
                                    danger
                                    icon={<StopOutlined/>}
                                    loading={cancelling === job.Id}
                                    disabled={cancelling !== "" && cancelling !== job.Id}
                                    aria-label="Отменить формирование отчёта"
                                >
                                    Отменить
                                </Button>
                            </Popconfirm>
                        )}
                    </Flex>
                </Flex>

                {stages ? (
                    <div>
                        <Flex justify="space-between" align="baseline" gap="2px 16px" wrap="wrap">
                            <Text strong aria-live="polite">{stageLine}</Text>
                            {eta !== null && (
                                <Text type="secondary" className="arp-tabular"><HourglassOutlined/> {fmtEta(eta)}</Text>
                            )}
                        </Flex>
                        <Progress
                            percent={pct ?? 0}
                            status={queued ? "normal" : "active"}
                            strokeColor={BAR_GRADIENT}
                            size={{height: 12}}
                            aria-label="Общий прогресс формирования отчёта"
                        />
                    </div>
                ) : (
                    <Flex vertical gap="4px">
                        <Text strong aria-live="polite">{queued ? "Ожидает запуска" : job.StageLabel || "Выполняется"}</Text>
                        {legacyPct !== null ? (
                            <Tooltip title={`${fmtInt(job.Progress?.Done)} из ${fmtInt(legacyTotal)}`}>
                                <div>
                                    <Progress percent={legacyPct} status="active" strokeColor={BAR_GRADIENT}
                                              aria-label="Прогресс текущего этапа"/>
                                </div>
                            </Tooltip>
                        ) : (
                            <Text type="secondary">Статус обновляется автоматически.</Text>
                        )}
                    </Flex>
                )}

                {stages && (
                    <ConfigProvider theme={{components: {Steps: {descriptionMaxWidth: 200}}}}>
                        <Steps
                            items={buildStepItems(stages, job, typical, serverNow)}
                            current={Math.max(0, stages.findIndex((s) => s.Status === "running"))}
                            direction={compact ? "vertical" : "horizontal"}
                            size={compact ? "small" : "default"}
                            responsive={false}
                        />
                    </ConfigProvider>
                )}

                {stages && !queued && <Counters job={job} isReady={isReady} counters={counters}/>}

                {events.length > 0 && <EventFeed events={events} title="Ход работы"/>}
            </Flex>
        );
    };

    const dismissButton = (
        <Button type="link" onClick={onDismiss} aria-label="Скрыть панель результата">Скрыть</Button>
    );

    const renderDone = () => {
        const dur = jobDurationSec(job);
        const breakdown = visibleStages
            .map((s) => {
                const d = stageDurationSec(s);
                return d !== null ? `${s.Label || s.Key} ${fmtDuration(d)}` : "";
            })
            .filter(Boolean)
            .join(" · ");
        const finishedAt = fmtHourMin(job.FinishedAt);
        return (
            <Flex vertical gap="18px">
                <Result
                    status="success"
                    style={{padding: compact ? "4px 0 0" : "8px 0 0"}}
                    title={dur !== null ? `Отчёт готов за ${fmtDuration(dur)}` : "Отчёт готов"}
                    subTitle={[periodLabel, finishedAt && `готов в ${finishedAt} МСК`, job.CreatedBy && `запустил ${job.CreatedBy}`]
                        .filter(Boolean)
                        .join(" · ")}
                    extra={
                        <Flex gap="8px 12px" justify="center" align="center" wrap="wrap">
                            {job.HasFile ? (
                                <Tooltip title={job.Filename || undefined}>
                                    <Button type="primary" size="large" icon={<DownloadOutlined/>}
                                            href={downloadHref(job)} target="_blank" rel="noreferrer">
                                        Скачать Excel
                                    </Button>
                                </Tooltip>
                            ) : (
                                <Text type="secondary">Файл отчёта недоступен</Text>
                            )}
                            {dismissButton}
                        </Flex>
                    }
                />
                <Counters job={job} isReady={isReady} counters={counters}/>
                {breakdown && (
                    <Text type="secondary" style={{fontSize: 13, textAlign: "center"}}>По этапам: {breakdown}</Text>
                )}
                <WarningsAlert warnings={warnings}/>
            </Flex>
        );
    };

    const renderFailed = () => (
        <Flex vertical gap="18px">
            <Result
                status="error"
                style={{padding: compact ? "4px 0 0" : "8px 0 0"}}
                title="Не удалось сформировать отчёт"
                subTitle={job.Message || "Причина не указана"}
                extra={dismissButton}
            />
            <WarningsAlert warnings={warnings}/>
            {events.length > 0 && <EventFeed events={events} title="Последние события"/>}
        </Flex>
    );

    const renderCancelled = () => (
        <Result
            status="info"
            style={{padding: compact ? "4px 0" : "8px 0"}}
            title="Формирование отчёта отменено"
            subTitle={[job.Message, periodLabel].filter(Boolean).join(" · ")}
            extra={dismissButton}
        />
    );

    let body;
    if (mode === "live") body = renderLive();
    else if (mode === "done") body = renderDone();
    else if (mode === "failed") body = renderFailed();
    else body = renderCancelled();

    return (
        <section
            style={{width: "100%", maxWidth: 1100}}
            aria-label={active ? "Ход формирования отчёта" : "Результат формирования отчёта"}
        >
            <style>{PANEL_CSS}</style>
            <Card style={PANEL_STYLES[mode]} styles={{body: {padding: 0}}}>
                {mode === "live" && !queued && <div className="arp-topline" aria-hidden="true"/>}
                <div ref={wrapRef} style={{padding: pad}}>
                    {body}
                </div>
            </Card>
        </section>
    );
}
